import type { DealKind } from '@carcheck/shared';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DealKindChips } from './DealKindChips';

/**
 * AUT-542 — one step of the `deals.stages` form: label, kind chips,
 * up / down, «Скрыт». No delete — a step is retired, never removed.
 * AUT-633 — kind chips from `DEAL_KINDS` and «Нужны фото»
 * (`requiresAct`), closed where the rules say so (RULINGS 7, 14).
 */

export interface DealStageRowProps {
  label: string;
  kinds: readonly string[];
  requiresAct: boolean;
  retired: boolean;
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  /** The label field takes the focus on mount (a step just added). */
  autoFocus: boolean;
  canToggleKind: (kind: DealKind) => boolean;
  canToggleRequiresAct: boolean;
  onLabel: (label: string) => void;
  onToggleKind: (kind: DealKind) => void;
  onMove: (by: -1 | 1) => void;
  onRequiresAct: (on: boolean) => void;
  onRetired: (retired: boolean) => void;
}

export const DealStageRow = ({
  label,
  kinds,
  requiresAct,
  retired,
  isFirst,
  isLast,
  busy,
  autoFocus,
  canToggleKind,
  canToggleRequiresAct,
  onLabel,
  onToggleKind,
  onMove,
  onRequiresAct,
  onRetired,
}: DealStageRowProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <TableRow sx={{ opacity: retired ? 0.55 : 1 }}>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={label}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the step the admin just added
          autoFocus={autoFocus}
          onChange={(e) => onLabel(e.target.value)}
          disabled={busy}
          slotProps={{ htmlInput: { 'aria-label': t('config.stages.labelInput') } }}
        />
      </TableCell>
      <TableCell>
        <DealKindChips
          kinds={kinds}
          busy={busy}
          canToggle={canToggleKind}
          onToggle={onToggleKind}
        />
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          aria-label={t('config.stages.up')}
          disabled={busy || isFirst}
          onClick={() => onMove(-1)}
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={t('config.stages.down')}
          disabled={busy || isLast}
          onClick={() => onMove(1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        <Switch
          size="small"
          checked={requiresAct}
          disabled={busy || !canToggleRequiresAct}
          onChange={(e) => onRequiresAct(e.target.checked)}
          slotProps={{ input: { 'aria-label': t('config.stages.columns.requiresAct') } }}
        />
      </TableCell>
      <TableCell>
        <Switch
          size="small"
          checked={retired}
          disabled={busy}
          onChange={(e) => onRetired(e.target.checked)}
          slotProps={{ input: { 'aria-label': t('config.stages.columns.retired') } }}
        />
      </TableCell>
    </TableRow>
  );
};
